import axios from "axios";
import {environmentConfig} from "@/config/enviromentConfig";
import {parseStringPromise} from "xml2js";
import {triviaClient} from "@/api/client";
import {getCaptchaToken, WIDGET_ID} from "@/functions/turnstile";
import {buildGameEndConfig} from "@/functions/gameEndCaptcha";
import {captureAttemptToken, withAttemptToken} from "@/functions/attemptToken";

async function gameStartCaptchaConfig(gameStart, params) {
    const token = gameStart ? await getCaptchaToken("game_start") : null;
    let headers = token
        ? {"X-Captcha-Token": token, "X-Captcha-Action": "game_start", "X-Captcha-Widget": WIDGET_ID}
        : undefined;

    if (!gameStart) {
        headers = withAttemptToken(headers);
    }

    return headers ? {params, headers} : {params};
}

async function gameEndCaptchaConfig(gameEnd) {
    const token = gameEnd ? await getCaptchaToken("game_end") : null;
    const captchaConfig = buildGameEndConfig(gameEnd, token, WIDGET_ID);
    const headers = withAttemptToken(captchaConfig?.headers);

    return headers ? {headers} : undefined;
}

export default class TriviaService {
    static async getRssXml(url) {
        const response = await axios.get(`${environmentConfig.utilsService}/utils/proxy/?endpoint=${url}`, {})

        if (!response.data) {
            throw new Error("Invalid response from proxy");
        }

        const jsonData = await parseStringPromise(response.data);

        return {
            data: jsonData,
        };
    }

    static async showPopup(msisdn, service, popupName) {
        const response = await triviaClient.get(`/game/show-popup`, {
            params: {
                service,
                popUpName: popupName,
            },
        })
        return response.data;
    }

    static async getUserStats(msisdn, productId, service) {
        const response = await triviaClient.get(`/game/get-user-stats`, {
            params: {
                productId,
                service,
            },
        })
        return response.data;
    }

    static async getUserStatsV2(encryptedMsisdn, productId, service) {
        const response = await triviaClient.get(`/game/get-user-stats`, {
            params: {
                productId,
                service,
            },
        })
        return response.data;
    }

    static async getTriviaQuestions(msisdn, questionCategoryId, questionGenreId, productId, isBonusRound, service, gameStart = false) {
        const response = await triviaClient.get(`/game/get-questions`, await gameStartCaptchaConfig(gameStart, {
            amountOfQuestions: 1,
            questionCategoryId,
            questionGenreId,
            productId,
            bonusGame: isBonusRound ? 1 : 0,
            service,
        }))

        if (gameStart) captureAttemptToken(response.data);

        return response.data;
    }

    static async getTriviaQuestionsV2(encryptedMsisdn, questionCategoryId, productId, isBonusRound, service, gameStart = false) {
        const response = await triviaClient.get(`/game/get-questions`, await gameStartCaptchaConfig(gameStart, {
            amountOfQuestions: 1,
            questionCategoryId,
            productId,
            bonusGame: isBonusRound ? 1 : 0,
            service,
        }))

        if (gameStart) captureAttemptToken(response.data);

        return response.data;
    }

    static async getLeaderboard(msisdn, service, productId) {
        const response = await triviaClient.get(`/game/get-leaderboard`, {
            params: {
                productId,
                numberRows: 20,
                service,
            },
        })
        return response.data;
    }

    static async getLeaderboardV2(encryptedMsisdn, service, productId) {
        const response = await triviaClient.get(`/game/get-leaderboard`, {
            params: {
                productId,
                numberRows: 10,
                service,
            },
        })
        return response.data;
    }

    static async submitUserAnswer(answer, productId, gameId, isBonusRound, service, gameEnd = false) {
        const response = await triviaClient.post(`/game/save-answer`, {
            answer,
            productId,
            gameId,
            bonusGame: isBonusRound ? 1 : 0,
            service,
        }, await gameEndCaptchaConfig(gameEnd))

        return response.data;
    }

    static async addFunds(msisdn, productId, gameFundsTypeId, gameFundsAllocationTypeId, amount, fromGameId) {
        const response = await triviaClient.post(`/dep/add-game-funds`, {
            msisdn,
            productId,
            gameFundsTypeId,
            gameFundsAllocationTypeId,
            value: amount,
            fromGameId,
        })
        return response.data;
    }

    static async addFundsV2(encryptedMsisdn, productId, gameFundsTypeId, gameFundsAllocationTypeId, amount, fromGameId) {
        const response = await triviaClient.post(`/dep/add-game-funds`, {
            productId,
            gameFundsTypeId,
            gameFundsAllocationTypeId,
            value: amount,
            fromGameId,
        })
        return response.data;
    }

    static async profanityChecker(usernameText) {
        return await axios.get(`https://www.purgomalum.com/service/containsprofanity?text=${usernameText}`, {})
    }

    static async getGameState(msisdn, service) {
        const response = await triviaClient.get(`/game/get-game-state`, {
            params: {
                service,
            },
        })
        return response.data;
    }

    static async getGameStateV2(encryptedMsisdn, service) {
        const response = await triviaClient.get(`/game/get-game-state`, {
            params: {
                service,
            },
        })
        return response.data;
    }

    static async startGame(msisdn, gameId, bonusGame) {
        const response = await triviaClient.post(`/game/start-game`, {
            gameId,
            bonusGame,
        })
        return response.data;
    }

    static async startGameV2(encryptedMsisdn, gameId, bonusGame) {
        const response = await triviaClient.post(`/game/start-game`, {
            gameId,
            bonusGame,
        })
        return response.data;
    }

    static async pauseGame(msisdn, service) {
        const response = await triviaClient.post(`/game/pause-game`, {
            service,
        })
        return response.data;
    }

    static async pauseGameV2(encryptedMsisdn, service) {
        const response = await triviaClient.post(`/game/pause-game`, {
            service,
        })
        return response.data;
    }

    static async endGame(gameId, processResults) {
        const headers = withAttemptToken(undefined);
        const response = await triviaClient.post(`/game/end-game`, {
            gameId,
            processResults,
        }, headers ? {headers} : undefined)

        return response.data;
    }

    static async getBadges(msisdn, service) {
        const response = await triviaClient.get(`/game/get-badges`, {
            params: {
                service,
            },
        })
        return response.data;
    }

    static async getPrizeEntries(msisdn, service) {
        const response = await triviaClient.get(`/game/get-prize-entries`, {
            params: {
                service,
            },
        })
        return response.data;
    }

    static async getRewardsClaimedStats(service) {
        const response = await triviaClient.get(`/game/get-rewards-claimed-stats`, {
            params: {
                service,
            },
        })

        return response.data?.[0];
    }

    static async claimReward(msisdn, badgeId, service) {
        const response = await triviaClient.post(`/game/claim-reward`, {
            badgeId,
            service,
        })
        return response.data;
    }
}
